import React, { useEffect, useRef, useState } from 'react';
import noUiSlider, {API} from 'nouislider';
// import 'nouislider/distribute/nouislider.css';
declare global {
    interface HTMLDivElement {
        noUiSlider?: API;
    }
}
interface RangeSliderProps {
    min: number;
    max: number;
    step?: number;
    defaultValue?: [number, number];  // defaultValue is optional
    onChange: (values: [number, number]) => void;
    textFormat?: (value: number) => string; // Optional format function for the values
    suffix?: string | ((value: [number, number]) => string); // Optional suffix that can be a string or a function
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
                                                            min,
                                                            max,
                                                            step = 1,
                                                            defaultValue,
                                                            onChange,
                                                            textFormat,
                                                            suffix = "", // Default is empty string if no suffix is provided
                                                        }) => {
    const sliderRef = useRef<HTMLDivElement| null>(null);
    const fromInputRef = useRef<HTMLInputElement | null>(null);
    const toInputRef = useRef<HTMLInputElement | null>(null);
    const fromTextRef = useRef<HTMLSpanElement | null>(null);
    const toTextRef = useRef<HTMLSpanElement | null>(null);

    // Use defaultValue if provided, otherwise fallback to [min, max]
    const initialValue: [number, number]  = defaultValue && defaultValue.length === 2 ? defaultValue : [min, max];
    const [currentValues, setCurrentValues] = useState<[number, number]>(initialValue);

    // Helper function to handle suffix
    const getSuffix = (value: [number, number]): string => {
        if (typeof suffix === 'function') {
            return suffix(value); // If suffix is a function, call it with the current value
        }
        return suffix; // Else, just return the static suffix string
    };

    // Handle slider update and input changes
    const handleUpdate = (values: (string | number)[]) => {
        const [fromVal, toVal] = values.map(Number);

        // Only update state if values have changed to avoid unnecessary re-renders
        if (fromVal !== currentValues[0] || toVal !== currentValues[1]) {
            setCurrentValues([fromVal, toVal]);

            if (fromInputRef.current) fromInputRef.current.value = fromVal.toLocaleString();
            if (toInputRef.current) toInputRef.current.value = toVal.toLocaleString();

        }
    };

    useEffect(() => {
        const slider = sliderRef.current;

        if (slider) {
            noUiSlider.create(slider, {
                range: {
                    min,
                    max,
                },
                start: initialValue,
                step,
                connect: true,
            });

            slider.noUiSlider?.on('update', (values: (string | number)[]) => handleUpdate(values));

            return () => {
                slider.noUiSlider?.destroy();
            };
        }
    }, [min, max, step, initialValue]);// Only re-run effect when min, max, or step changes

    // Handle input change and sync it with the slider
    const handleInputChange = (index: 0 | 1, value: string) => {
        const parsedValue = parseInt(value.replace(/\s+/g, ''), 10);
        setCurrentValues((prevValues) => {
            const newValues = [...prevValues];
            newValues[index] = parsedValue || 0;
            return newValues as [number, number];
        });
    };

    const handleApply = () => {
        onChange(currentValues); // Trigger onChange with the current state values
    };

    return (
        <div className="range js_range">
            <div className="range__fields js_range_fields">
                <input
                    ref={fromInputRef}
                    className="range__field js_range_from"
                    type="text"
                    data-min={min}
                    value={currentValues[0]} // Bind to currentValues for dynamic updates
                    onChange={(e) => handleInputChange(0, e.target.value)}
                />
                <input
                    ref={toInputRef}
                    className="range__field js_range_to"
                    type="text"
                    data-max={max}
                    value={currentValues[1]} // Bind to currentValues for dynamic updates
                    onChange={(e) => handleInputChange(1, e.target.value)}
                />
            </div>
            <div ref={sliderRef} className="range__slider js_range_slider"></div>
            <div className="range__values">
                <span ref={fromTextRef} className="range__value-text">
                    {textFormat ? textFormat(currentValues[0]) : currentValues[0]}
                </span>{' '}
                —{' '}
                <span ref={toTextRef} className="range__value-text">
                    {textFormat ? textFormat(currentValues[1]) : currentValues[1]}
                </span>
                {' '}{getSuffix(currentValues)}
            </div>
            <div className="range__btns">
                <button type="button" onClick={handleApply} className="btn btn_cta">
                    Apply
                </button>
            </div>
        </div>
    );
};
